import { getCoverageLinesText } from '@common/utils';
import { isEmpty } from 'lodash';
import moment from 'moment/moment';
import { Divider, Highlight, Stack, SxProps, Theme, Typography, TypographyProps } from '@common-components';
import { Warning } from '@icons';
import { SubmissionStatus } from 'enums';
import { CarrierSubmissionSearchItem } from 'types';
import { getTimezoneLessDisplayDate, hasMinutesPassed, timeSince, truncateMultiLineText, uniteStyles } from 'utils';
import IconBadge from 'components/IconBadge';
import { ProcessingAnimation } from 'components/ProcessingAnimation';
import { QueueColumns, queueColumnsConfig } from './config';

const OVERDUE_THRESHOLD_HOURS = 72;

interface QueueTableItemProps {
  item: CarrierSubmissionSearchItem;
  isInsuredNameExtracted?: boolean;
  isCoverageLinesExtracted?: boolean;
  searchInput?: string;
}

const getExtractionTypographyProps = (
  isExtracted: boolean,
  variant: TypographyProps['variant'] = 'body2',
): Pick<TypographyProps, 'variant' | 'color'> =>
  isExtracted ? { variant: `${variant}Bold` as TypographyProps['variant'], color: 'green.600' } : { variant };

const getExtractionSxProps = (isExtracted: boolean): SxProps<Theme> =>
  isExtracted ? { span: { bgcolor: 'green.100' } } : {};

export default function QueueTableItem({
  item,
  isInsuredNameExtracted,
  isCoverageLinesExtracted,
  searchInput = '',
}: QueueTableItemProps) {
  const { submission } = item;
  const isProcessing =
    submission?.status === SubmissionStatus.Processing && !hasMinutesPassed(submission.createdAt, 24);

  return (
    <>
      <Stack px={2} gap={1} direction="row" alignItems="stretch" height={72} position="relative">
        {isProcessing && <ProcessingAnimation />}
        <Stack flex={queueColumnsConfig[QueueColumns.Received].flex} justifyContent="center">
          <Stack direction="row" gap={1} alignItems="center">
            {submission?.createdAt &&
              moment().diff(moment(submission.createdAt), 'hours') >= OVERDUE_THRESHOLD_HOURS && (
                <IconBadge size="extraSmall" IconComponent={Warning} bgcolor="warning.main" />
              )}
            <Typography variant="body2" noWrap>
              {submission?.createdAt ? timeSince(submission.createdAt) : ''}
            </Typography>
          </Stack>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.InsuredName].flex} justifyContent="center" overflow="hidden">
          <Typography
            {...getExtractionTypographyProps(!!isInsuredNameExtracted)}
            overflow="hidden"
            sx={uniteStyles({ ...truncateMultiLineText(2) }, getExtractionSxProps(!!isInsuredNameExtracted))}
          >
            <Highlight searchWords={[searchInput]} textToHighlight={submission?.insuredName ?? ''} />
          </Typography>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.Agent].flex} justifyContent="center" overflow="hidden">
          <Typography variant="body2" sx={truncateMultiLineText(2)}>
            <Highlight searchWords={[searchInput]} textToHighlight={item.agentName ?? ''} />
          </Typography>
          <Typography variant="caption2" color="text.secondary">
            {item.agentNumber ?? ''}
          </Typography>
        </Stack>

        <Stack flex={queueColumnsConfig[QueueColumns.LineOfBusiness].flex} justifyContent="center" overflow="hidden">
          {!isEmpty(submission?.coverageLines) && (
            <Typography
              {...getExtractionTypographyProps(!!isCoverageLinesExtracted, 'caption')}
              component="div"
              sx={uniteStyles({ ...truncateMultiLineText(2) }, getExtractionSxProps(!!isCoverageLinesExtracted))}
            >
              <Highlight
                searchWords={[searchInput]}
                textToHighlight={getCoverageLinesText(submission?.coverageLines) || ''}
              />
            </Typography>
          )}
        </Stack>

        {/* TODO: render status */}
        <Stack flex={queueColumnsConfig[QueueColumns.Status].flex} justifyContent="center" />

        <Stack flex={queueColumnsConfig[QueueColumns.EffectiveDate].flex} justifyContent="center">
          <Typography variant="body2" noWrap>
            {submission?.dueDate ? getTimezoneLessDisplayDate(submission.dueDate) : ''}
          </Typography>
        </Stack>

        {/* TODO: render triage output */}
        <Stack flex={queueColumnsConfig[QueueColumns.TriageOutput].flex} justifyContent="center" />

        {/* TODO: render STP */}
        <Stack flex={queueColumnsConfig[QueueColumns.Stp].flex} justifyContent="center" />
      </Stack>
      <Divider />
    </>
  );
}
